// React et hooks
import { useCallback, useEffect, useMemo, useState, useRef } from "react";

// MUI
import { Box, Paper, Button, Typography, useTheme, Skeleton, Stack, IconButton, Menu, MenuItem } from "@mui/material";
import { DataGridPro, GridColDef, GridSortModel, useGridApiRef } from '@mui/x-data-grid-pro';

// Redux
import { useSelector, useDispatch } from "react-redux";
import { setFilterDate, setFilterDateCompare } from "src/context/reducers/filter-settings/filterSlice";

// Context et reducers
import { useCurrentWorkspace } from "src/context/reducers/app-settings";

// API queries
import {
    useGetVisibilityKpisQuery,
    useGetCompetitionEvolutionVisibilityCardQuery,
} from "src/context/api/competitorsMonitoring/api";
import { useGetLocalKpisQuery } from "src/context/api/visibility/api";

// Localisation
import { useLocales } from "src/locales";

// Utilitaires
import { fDate } from "src/utils/format-time";
import { subDays, subWeeks, subYears } from "date-fns";
import countriesJson from "src/utils/countries.json";
import { SplashScreen } from 'src/components/loading-screen';
import { useLocation } from 'react-router-dom';
import { useCountryData } from "src/components/visibility/hooks/useCountryData";

import { PresenceSegments } from "src/components/visibility/types";
import { createDataGridColumns } from "src/components/visibility/utils/datagrid-config";

// Composants
import PresenceRateChart from 'src/components/visibility/components/PresenceRateChart';
import { CardBar } from 'src/components/visibility/components/CardBar';
import type { TopCompetitorStat } from 'src/components/visibility/components/TopCompetitorsCard';

// Hooks personnalisés
import { useResponsiveConfig } from "src/components/visibility/utils/use-responsive-config";

// Styles
import "src/components/visibility/style.css";
import { CountryBar as CountryFilter } from 'src/components/country-bar';
import Iconify from "src/components/iconify";

/**
 * Visibility > Local.
 *
 * Deliberately the Search Term page with the location as its dimension: same
 * country bar, same cards, same columns. The date range is the exception: this
 * page carries its own period and comparison dropdowns in the grid toolbar, and
 * the header date picker is hidden for it. Only three
 * things change — the grid reads sql/local_analysis.sql (one row per top level
 * location instead of one per search term), the toolbar carries a Locations
 * filter where Search Term carries its ABS toggle, and the location marts carry
 * no traffic share, so the first column shows the location type instead.
 */
// Figma 6848:11211 (footer de pagination) : color/azure/62 pour le texte, le même
// à 20% pour les bordures. Valeurs alignées sur EXPLORE_COLORS, qui suit la même
// maquette — pas de token partagé pour l'instant, d'où la duplication locale.
const FOOTER_BG = '#FAFAFA';
const FOOTER_TEXT = '#919EAB';
const GRID_BORDER = 'rgba(145, 158, 171, 0.2)';
// Figma 6848:10223 (toolbar) : color/azure/24 pour le libellé des sélecteurs,
// #EAEAEA pour le filet sous la toolbar.
const TOOLBAR_LABEL = '#333C47';
const TOOLBAR_BORDER = '#EAEAEA';

/**
 * Sélecteur de la toolbar — Figma 6848:10223.
 *
 * Pas un champ de saisie : une boîte bordée qui contient une pill grise portant le
 * libellé, puis un chevron. Un `Autocomplete` ou un `Select` MUI ne donne pas cette
 * structure (leur fond et leur bordure sont sur le même élément), d'où ce composant.
 */
const ToolbarSelect = <T extends { label: string; value: string }>({
    value,
    options,
    onChange,
}: {
    value: T;
    options: T[];
    onChange: (option: T) => void;
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    return (
        <>
            <Box
                onClick={(event) => setAnchorEl(event.currentTarget)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    p: '3px',
                    // Le node fait 30 de haut : 3 + pill 24 + 3. Un `border` CSS
                    // s'ajouterait à ces 30 et donnerait 32, alors que le stroke
                    // Figma est dessiné à l'intérieur du cadre. D'où l'anneau
                    // interne, qui ne participe pas au layout.
                    height: 30,
                    boxSizing: 'border-box',
                    boxShadow: `inset 0 0 0 1px ${GRID_BORDER}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 24,
                        px: '8px',
                        borderRadius: '6px',
                        backgroundColor: 'rgba(145, 158, 171, 0.32)',
                        maxWidth: 146,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '13px',
                            fontWeight: 500,
                            lineHeight: '23px',
                            color: TOOLBAR_LABEL,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {value.label}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', p: '2px' }}>
                    <Iconify icon="eva:chevron-down-fill" width={18} height={18} sx={{ color: TOOLBAR_LABEL }} />
                </Box>
            </Box>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {options.map((option) => (
                    <MenuItem
                        key={option.value}
                        selected={option.value === value.value}
                        onClick={() => {
                            onChange(option);
                            setAnchorEl(null);
                        }}
                        sx={{ fontSize: '13px' }}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

interface DateRangeOption {
    label: string;
    value: string;
    days: number;
}

interface ComparisonOption {
    label: string;
    value: 'none' | 'previous_period' | 'last_year';
}

const DATE_RANGE_OPTIONS: DateRangeOption[] = [
    { label: 'Last 7 Days', value: 'last7d', days: 7 },
    { label: 'Last 14 Days', value: 'last14d', days: 14 },
    { label: 'Last 30 Days', value: 'last30d', days: 30 },
];

// Fenêtre des trois cartes. Elles annoncent « 12 weeks » et le SQL de la carte
// visibilité code en dur `interval 12 week` depuis `current_date()` : le
// sélecteur de période de la toolbar ne les concerne pas.
const CARD_WINDOW_WEEKS = 12;

// `No comparison` first, and the default: the grid opens on the selected window
// alone. Picking one of the other two is what brings the evolution slots back.
const COMPARISON_OPTIONS: ComparisonOption[] = [
    { label: 'No comparison', value: 'none' },
    { label: 'Previous period', value: 'previous_period' },
    { label: 'Last year', value: 'last_year' },
];

/**
 * Selected window and its comparison window, from the two toolbar dropdowns.
 *
 * `end` is yesterday, matching what the header used to default to: today is still
 * being collected, so including it would drag every rate down.
 */
const resolveWindows = (range: DateRangeOption, comparison: ComparisonOption) => {
    const end = subDays(new Date(), 1);
    const start = subDays(end, range.days - 1);

    if (comparison.value === 'none') {
        return { start, end, compareStart: null, compareEnd: null };
    }
    if (comparison.value === 'last_year') {
        return { start, end, compareStart: subYears(start, 1), compareEnd: subYears(end, 1) };
    }
    // Previous period: the same number of days, ending the day before `start`.
    const compareEnd = subDays(start, 1);
    return { start, end, compareStart: subDays(compareEnd, range.days - 1), compareEnd };
};

/**
 * Clé de persistance du pays sélectionné, partagée avec VisibilityProvider :
 * les deux résolvent la même sélection pour le même workspace, et la page doit
 * repartir sur le pays que l'utilisateur a laissé.
 */
const countryStorageKey = (workspaceId?: number | string | null) =>
    workspaceId ? `visibility.selectedCountry.${workspaceId}` : null;

const readStoredCountry = (workspaceId?: number | string | null): string | null => {
    const key = countryStorageKey(workspaceId);
    if (!key) return null;
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

export default function LocalVisibility() {
    const { t } = useLocales();
    const theme = useTheme();
    const location = useLocation();
    const prefilledFromQueryRef = useRef<boolean>(false);

    // Configuration responsive granulaire
    const responsiveConfig = useResponsiveConfig();

    const workspace: any = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);

    // Fetch countries data
    const { countriesData, isLoading } = useCountryData();

    const dispatch = useDispatch();

    // Valeur optimiste au montage. `skipLocalQuery` exige un pays : partir de
    // `null` fait attendre la réponse de /filters/countries-coverage avant même
    // d'émettre la requête de la grid, soit deux allers-retours en série. Le pays
    // stocké est validé dès que la liste arrive, et corrigé s'il n'y est plus —
    // au pire une requête de trop, au mieux un aller-retour économisé.
    //
    // `workspace` est garanti présent au montage : WorkspaceGuard affiche un
    // splash tant que la liste n'est pas chargée. S'il manquait, on retomberait
    // simplement sur l'ancien comportement.
    const [selectedCountry, setSelectedCountryState] = useState<string | null>(() =>
        readStoredCountry(workspace?.id)
    );

    const setSelectedCountry = useCallback((country: string | null) => {
        setSelectedCountryState(country);
        const key = countryStorageKey(workspace?.id);
        if (!key) return;
        try {
            if (country) localStorage.setItem(key, country);
            else localStorage.removeItem(key);
        } catch {
            // Stockage indisponible (mode privé, quota) : la sélection reste en
            // mémoire, seule la reprise au rechargement est perdue.
        }
    }, [workspace?.id]);
    const [dateRangeOption, setDateRangeOption] = useState<DateRangeOption>(DATE_RANGE_OPTIONS[0]);
    const [comparisonOption, setComparisonOption] = useState<ComparisonOption>(COMPARISON_OPTIONS[0]);
    const isComparing = comparisonOption.value !== 'none';

    // The page owns its dates: the header date picker is hidden here and the grid
    // reads its window from the store, so the two dropdowns publish there rather
    // than keeping a second, divergent source of truth. The three cards are not
    // concerned — they have their own fixed 12 week window, see `cardWindow`.
    // `skipAlignment` because a preset is an exact request — snapping it to the
    // granularity inherited from another page would silently shift the window.
    useEffect(() => {
        const { start, end, compareStart, compareEnd } = resolveWindows(dateRangeOption, comparisonOption);
        dispatch(setFilterDate({ start: start.toISOString(), end: end.toISOString(), skipAlignment: true }));
        // Nothing published without a comparison window: the store keeps whatever
        // another page left there, and `isComparing` is what decides whether the
        // grid asks for it.
        if (compareStart && compareEnd) {
            dispatch(setFilterDateCompare({
                compareStart: compareStart.toISOString(),
                compareEnd: compareEnd.toISOString(),
                skipAlignment: true,
            }));
        }
    }, [dateRangeOption, comparisonOption, dispatch]);

    // `isLoading` (la liste des pays) n'est délibérément pas un motif de skip : il
    // mettait la grid derrière /filters/countries-coverage alors qu'elle n'a besoin
    // que d'un nom de pays. Avec la valeur optimiste lue au montage, les deux
    // requêtes partent ensemble ; sans pays stocké, `!selectedCountry` suffit à
    // attendre.
    const skipLocalQuery = !filterStore.start || !filterStore.end || !workspace?.id || !selectedCountry;

    // `geotargets` non transmis : le serveur retombe alors sur toutes les
    // localisations configurées du workspace, ce qui est le scope de la page.
    // No comparison window sent at all on `No comparison`: the backend then returns
    // every `*_previous` field as NULL rather than computing a window nobody shows.
    const { data: localKpisResponse, isFetching: isFetchingLocalKpis } = useGetLocalKpisQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        compareStartDate: isComparing && filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, "yyyy-MM-dd") : null,
        compareEndDate: isComparing && filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, "yyyy-MM-dd") : null,
        devices: filterStore.devices,
        platforms: filterStore?.platforms || [],
        countries: selectedCountry ? [selectedCountry] : filterStore.countries,
        keywords: filterStore.keywords,
        workspaceId: workspace?.id,
    }, {
        skip: skipLocalQuery,
    });

    // Les 3 cartes sont celles des autres pages Visibility : mêmes requêtes,
    // même fenêtre fixe de 12 semaines, scopées pays et non localisation.
    //
    // Cette fenêtre est calculée une seule fois, et surtout pas depuis le store :
    // les deux listes de la toolbar y publient la période de la grid, et RTK Query
    // indexe son cache sur les arguments. Des dates qui changent, ce sont trois
    // requêtes relancées à chaque changement de période pour un résultat
    // identique — le SQL des cartes ne lit même pas ces dates.
    const cardWindow = useMemo(() => {
        const end = subDays(new Date(), 1);
        const start = subWeeks(end, CARD_WINDOW_WEEKS);
        return {
            startDate: fDate(start, 'yyyy-MM-dd'),
            endDate: fDate(end, 'yyyy-MM-dd'),
            compareStartDate: fDate(subWeeks(start, CARD_WINDOW_WEEKS), 'yyyy-MM-dd'),
            compareEndDate: fDate(subDays(start, 1), 'yyyy-MM-dd'),
        };
    }, []);

    // Les cartes ne dépendent plus de la période du store, seulement du workspace
    // et du pays.
    //
    // `isLoading` — le `isFetching` de /filters/countries-coverage — n'est
    // délibérément pas un motif de skip, pour la même raison que sur la grid : cet
    // endpoint est scopé par la période, donc changer de preset le refait partir, et
    // le garder ici coupait l'abonnement des deux requêtes de cartes puis le
    // rétablissait. Les cartes repartaient en requête à chaque changement de date,
    // alors que leur fenêtre de 12 semaines n'avait pas bougé. Un pays sélectionné
    // suffit : c'est la seule chose que la liste leur apporte.
    const skipCardQuery = !workspace?.id || !selectedCountry;

    const { data: visibilityCardData, isFetching: isFetchingVisibilityCard } = useGetCompetitionEvolutionVisibilityCardQuery({
        startDate: cardWindow.startDate,
        endDate: cardWindow.endDate,
        devices: filterStore.devices || [],
        keywords: filterStore.keywords || [],
        platforms: filterStore?.platforms || [],
        workspaceId: workspace?.id,
        selectedCountry,
    }, {
        skip: skipCardQuery,
    });

    const { data: cardBarKpisData } = useGetVisibilityKpisQuery({
        startDate: cardWindow.startDate,
        endDate: cardWindow.endDate,
        compareStartDate: cardWindow.compareStartDate,
        compareEndDate: cardWindow.compareEndDate,
        compare: filterStore?.compare,
        timestep: filterStore.timestep,
        devices: filterStore.devices,
        countries: selectedCountry ? [selectedCountry] : filterStore.countries,
        keywords: filterStore.keywords,
        platforms: filterStore?.platforms,
        workspaceId: workspace?.id,
    }, {
        skip: skipCardQuery,
    });

    const loadedCountries = Array.isArray(filterStore?.loadedCountries) ? filterStore.loadedCountries : [];

    // Initialisation pays: query param > premier pays disponible
    useEffect(() => {
        // 0) Rien à valider tant que la liste des pays n'est pas arrivée. Sans ces
        // deux garde-fous, la valeur optimiste lue au montage serait remplacée par
        // un pays de `loadedCountries` — que le store ne réinitialise jamais au
        // changement de workspace — puis remise dès la réponse : une requête de
        // grid pour rien, et un scintillement de la barre.
        if (isLoading) return;
        if (countriesData.length === 0 && selectedCountry) return;

        // 1) La sélection courante est toujours dans la liste : il n'y a rien à
        // faire. Ce garde-fou passe en premier parce que l'effet dépend de
        // `selectedCountry` et repart donc à chaque clic dans la barre — réévaluer
        // le deep link à ce moment-là réappliquerait le pays de l'URL et la
        // sélection reviendrait en arrière aussitôt. Même ordre que
        // VisibilityProvider, qui résout la même initialisation.
        const stillAvailable =
            !!selectedCountry && countriesData.some((c) => c.country_name === selectedCountry);
        if (stillAvailable) return;

        // 2) Deep link via ?country=FR (code ISO → nom), une seule fois : passé
        // l'initialisation, c'est la sélection de l'utilisateur qui fait foi.
        if (!prefilledFromQueryRef.current) {
            const params = new URLSearchParams(location.search || (typeof window !== 'undefined' ? window.location.search : ''));
            const codeParam = params.get('country') || params.get('c');
            if (codeParam && countriesData.length > 0) {
                const mapped = countriesJson?.find((c) => String(c.code).toUpperCase() === String(codeParam).toUpperCase())?.name;
                if (mapped && countriesData.some((c) => c.country_name === mapped)) {
                    prefilledFromQueryRef.current = true;
                    setSelectedCountry(mapped);
                    return;
                }
            }
        }
        // 3) Fallback: sélectionner le premier pays disponible. On repasse aussi par
        // là quand le pays courant n'est plus dans la liste (changement de période ou
        // de workspace) : sinon la barre reste sur un pays absent, donc sans sélection.
        const fallbackFromCi = countriesData.length > 0 ? countriesData[0].country_name : null;
        const fallbackFromLoaded = loadedCountries.find((name: string) => typeof name === 'string' && name.trim());
        const fallback = fallbackFromCi ?? fallbackFromLoaded ?? null;
        if (fallback) {
            setSelectedCountry(fallback);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, countriesData, loadedCountries, selectedCountry, isLoading, setSelectedCountry]);

    // Controlled sorting with error handling for rapid page switching
    const apiRef: any = useGridApiRef();
    // Tri par défaut : taux de visibilité décroissant. Search Term trie sur son
    // traffic share, que les marts locales ne portent pas.
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'blendedPresenceRate', sort: 'desc' }]);
    const [hasError, setHasError] = useState(false);

    // Cleanup apiRef on component unmount to prevent ARIA errors during rapid switching
    useEffect(() => {
        return () => {
            if (apiRef.current) {
                try {
                    apiRef.current.setSortModel([]);
                } catch (error) {
                    // Silently ignore cleanup errors to prevent console spam
                }
            }
        };
    }, [apiRef]);

    // Page-max the Ad cost / Ad clicks bars scale to. Computed on the payload
    // rather than per cell: the bars are only comparable against a single maximum.
    const adMetricsMax = useMemo(() => {
        const items = (localKpisResponse?.data as any[] | undefined) ?? [];
        const maxOf = (key: string) => items.reduce(
            (acc: number, item: any) => Math.max(acc, typeof item?.[key] === 'number' ? item[key] : 0),
            0,
        );
        // Live Google Ads figures, not the national `ad_cost` / `ad_clicks` of the
        // marts: on Local the columns are fed by region_performance.
        return { adCost: maxOf('gads_cost'), adClicks: maxOf('gads_clicks') };
    }, [localKpisResponse?.data]);

    // Create columns for the DataGrid using externalized configuration
    // Filtré ici plutôt que via une option de `createDataGridColumns` : la config
    // est partagée avec les autres pages Visibility, et seule celle-ci porte un
    // sélecteur de comparaison.
    //
    // « New competitors » n'a de sens que face à une période de référence — hors
    // comparaison les lignes sortent déjà vides (voir `newCompetitors` plus bas),
    // et une colonne de tirets se lit comme « aucun entrant » au lieu de
    // « question non posée ». Retirée de `columns` et non masquée via
    // `columnVisibilityModel` : celui-ci n'est lu qu'au montage, donc changer de
    // comparaison ensuite ne la ferait pas réapparaître.
    const columns: GridColDef[] = createDataGridColumns({
        responsiveConfig,
        theme,
        t,
        workspace,
        sortModel,
        // La colonne localisation n'a pas d'en-tête de traffic share à basculer.
        toggleTrafficShareSort: () => { },
        PresenceRateChart,
        columnType: 'location',
        cellMode: 'searchTermAnalysis',
        selectedCountry,
        adMetricsMax,
    }).filter((column) => isComparing || column.field !== 'newCompetitors');

    const rows = ((localKpisResponse?.data as any[] | undefined) ?? []).map((item: any, index: number) => {
        const allCompetitors = Array.isArray(item?.top_5_competitors) ? item.top_5_competitors : [];

        const topCompetitors = allCompetitors.map((c: any) => c?.domain_name).filter(Boolean);
        const topCompetitorsPaidRates: Record<string, number> = allCompetitors.reduce((acc: Record<string, number>, c: any) => {
            const domain = c?.domain_name;
            const rate = c?.paid_presence_rate;
            if (domain && typeof rate === 'number') acc[domain] = rate;
            return acc;
        }, {});
        const topCompetitorsTopSearchTerms: Record<string, string[]> = allCompetitors.reduce((acc: Record<string, string[]>, c: any) => {
            const domain = c?.domain_name;
            if (domain && Array.isArray(c?.top_5_st)) acc[domain] = c.top_5_st;
            return acc;
        }, {});

        const firstCompetitorDomain = allCompetitors.length > 0 && allCompetitors[0]?.domain_name
            ? allCompetitors[0].domain_name
            : undefined;

        return {
            id: `${item?.country_name ?? 'NA'}__${item?.top_level_location_name ?? index}`,
            location: item?.top_level_location_name ?? '',
            locationType: item?.top_level_location_type ?? '',
            country: item?.country_name ?? '',
            ai_overview_coverage: item?.ai_overview_coverage ?? 0,
            brandDomain: workspace?.url,
            firstCompetitorDomain,
            isDemoBrand: workspace?.name?.includes('Demo'),
            topCompetitors,
            topCompetitorsPaidRates,
            topCompetitorsTopSearchTerms,
            // Entrants du top 5 sur la période. Tableau vide quand rien n'a bougé et
            // quand la fenêtre de comparaison ne fournit pas de classement de
            // référence (le SQL rend NULL) : la cellule affiche un tiret dans les deux
            // cas.
            newCompetitors: isComparing && Array.isArray(item?.new_competitors) ? item.new_competitors : [],
            paidPresenceRate: item?.client_paid_presence_rate ?? 0,
            // Clics et coûts Google Ads Live, à la maille de localisation configurée
            // pour le client (`region_performance`). `null` — et non 0 — quand la
            // localisation n'a pas de correspondance Google Ads : le LEFT JOIN garde
            // la ligne, la cellule affiche un tiret.
            adCost: typeof item?.gads_cost === 'number' ? item.gads_cost : null,
            adClicks: typeof item?.gads_clicks === 'number' ? item.gads_clicks : null,
            // Fenêtre précédente : `null` quand elle n'existe pas, pour que la
            // cellule n'affiche aucun delta plutôt qu'un faux -100%. `No comparison`
            // est ce même cas, décidé côté page : le backend rend déjà les `_previous`
            // à NULL sans fenêtre, mais le glissement annuel, lui, est dérivé de la
            // période courante et survivrait au sélecteur.
            adCostPrevious: isComparing && typeof item?.gads_cost_previous === 'number' ? item.gads_cost_previous : null,
            adClicksPrevious: isComparing && typeof item?.gads_clicks_previous === 'number' ? item.gads_clicks_previous : null,
            // Même période un an plus tôt. Affichée en second delta dans la cellule,
            // avec l'évolution telle que le backend la calcule (`_delta`) : une
            // référence nulle ou absente n'a pas d'évolution, et la recalculer ici
            // ferait diverger les deux formules.
            adCostYearAgo: isComparing && typeof item?.gads_cost_year_ago === 'number' ? item.gads_cost_year_ago : null,
            adClicksYearAgo: isComparing && typeof item?.gads_clicks_year_ago === 'number' ? item.gads_clicks_year_ago : null,
            adCostYoy: isComparing && typeof item?.gads_cost_yoy === 'number' ? item.gads_cost_yoy : null,
            adClicksYoy: isComparing && typeof item?.gads_clicks_yoy === 'number' ? item.gads_clicks_yoy : null,
            aiOverviewCoveragePrevious: isComparing && typeof item?.ai_overview_coverage_previous === 'number'
                ? item.ai_overview_coverage_previous
                : null,
            blendedPresenceRate: item?.visibility_rate ?? 0,
            visibilityRatePrevious: isComparing ? (item?.visibility_rate_previous ?? null) : null,
            // Écart en points, déjà calculé côté SQL.
            visibilityRateEvolution: isComparing && typeof item?.visibility_rate_evolution === 'number'
                ? item.visibility_rate_evolution
                : null,
            firstCompetitorPaidPresenceRate: firstCompetitorDomain
                ? (allCompetitors.find((c: any) => c?.domain_name === firstCompetitorDomain)?.paid_presence_rate ?? 0)
                : 0,
            presenceRateData: {
                organicOnly: item?.organic_only ?? 0,
                soloBidder: item?.solo_bidder ?? 0,
                topAdPosition: item?.top_ad_position ?? 0,
            } as PresenceSegments,
        };
    });

    const hasNoData = !isFetchingLocalKpis && rows.length === 0;

    // Solo bidder rate card: series from visibility card; main KPI from total_weekly_solo_bidder_rate
    const soloBidderWeeklySeries = useMemo(() => {
        const series = visibilityCardData?.data?.visibility_rate_weekly_series
            ?? visibilityCardData?.data?.blended_presence_rate_weekly_series;
        if (!Array.isArray(series)) return undefined;
        return series.map((d: { week_start?: string; solo_bidder?: number }) => ({
            week_start: d?.week_start ?? '',
            solo_bidder: typeof d?.solo_bidder === 'number' ? d.solo_bidder : 0,
        }));
    }, [visibilityCardData?.data?.visibility_rate_weekly_series, visibilityCardData?.data?.blended_presence_rate_weekly_series]);

    const soloBidderValue = useMemo(() => {
        const total = visibilityCardData?.data?.total_weekly_solo_bidder_rate;
        if (typeof total === 'number' && !Number.isNaN(total)) return total;
        if (!Array.isArray(soloBidderWeeklySeries) || soloBidderWeeklySeries.length === 0) return undefined;
        const last = soloBidderWeeklySeries[soloBidderWeeklySeries.length - 1];
        const v = last?.solo_bidder;
        return typeof v === 'number' && !Number.isNaN(v) ? v : undefined;
    }, [visibilityCardData?.data?.total_weekly_solo_bidder_rate, soloBidderWeeklySeries]);

    // Card top 3 compet: comes from main_overview.sql payload
    const topCompetitorsData = useMemo<TopCompetitorStat[]>(() => {
        const firstItem = (cardBarKpisData as any[] | undefined)?.[0];
        const top3FromBackend =
            firstItem?.top_3_competitors
            ?? firstItem?.top_3_compet
            ?? firstItem?.card_top_3_competitors;
        if (Array.isArray(top3FromBackend) && top3FromBackend.length > 0) {
            return top3FromBackend
                .map((item: any) => ({
                    domain: String(item?.domain_name ?? item?.domain ?? '').trim(),
                    paidPresenceRate: typeof item?.paid_presence_rate === 'number' ? item.paid_presence_rate : 0,
                }))
                .filter((item: TopCompetitorStat) => item.domain.length > 0)
                .sort((a: TopCompetitorStat, b: TopCompetitorStat) => b.paidPresenceRate - a.paidPresenceRate)
                .slice(0, 3);
        }

        const stats = new Map<string, { total: number; count: number }>();
        const workspaceDomains = new Set<string>();
        if (workspace?.url) {
            workspaceDomains.add(workspace.url.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, ''));
        }
        if (workspace?.friendlyDomains && Array.isArray(workspace.friendlyDomains)) {
            workspace.friendlyDomains.forEach((d: string) => {
                workspaceDomains.add(d.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, ''));
            });
        }
        (cardBarKpisData as any[] | undefined)?.forEach((item: any) => {
            if (!Array.isArray(item?.top_5_competitors)) return;
            item.top_5_competitors.forEach((competitor: any) => {
                const domain = competitor?.domain_name;
                const rate = competitor?.paid_presence_rate;
                if (!domain || typeof rate !== 'number' || rate <= 0) return;
                const normalizedDomain = domain.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
                if (workspaceDomains.has(normalizedDomain)) return;
                const current = stats.get(domain) ?? { total: 0, count: 0 };
                current.total += rate;
                current.count += 1;
                stats.set(domain, current);
            });
        });
        return Array.from(stats.entries())
            .map(([domain, { total, count }]) => ({
                domain: domain.trim(),
                paidPresenceRate: count ? Math.round((total / count) * 10) / 10 : 0,
            }))
            .filter((item: TopCompetitorStat) => item.domain.length > 0)
            .sort((a, b) => b.paidPresenceRate - a.paidPresenceRate)
            .slice(0, 3);
    }, [cardBarKpisData, workspace]);

    // Card visibility/solo bidder: comes from cest_card_visibility_rate.sql
    const blendedPresenceRateWeeklySeries = useMemo(() => {
        const series = visibilityCardData?.data?.visibility_rate_weekly_series;
        if (Array.isArray(series)) {
            return series;
        }
        return undefined;
    }, [visibilityCardData?.data?.visibility_rate_weekly_series]);

    // Top metric for visibility card from dedicated SQL
    const blendedPresenceTopMetric = useMemo(() => {
        const visibilityTotal = Number(visibilityCardData?.data?.visibility_rate_total ?? 0);
        if (visibilityTotal > 0) {
            return `${Math.round(visibilityTotal)}%`;
        }

        const validRates = rows
            .map((row) => row.blendedPresenceRate)
            .filter((rate): rate is number => typeof rate === 'number' && rate > Math.round(0));
        if (validRates.length > 0) {
            const averageRate = validRates.reduce((sum: number, rate: number) => sum + rate, 0) / validRates.length;
            return `${Math.round(averageRate)}%`;
        }
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibilityCardData?.data?.visibility_rate_total, rows]);

    if (!filterStore?.countries || !filterStore?.keywords || !filterStore?.devices) {
        return <SplashScreen />;
    }

    const CustomToolbar = () => {
        return (
            <Stack
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                direction="row"
                alignItems="center"
                spacing="12px"
                px={"20px"}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    backgroundColor: 'white',
                    borderTopRightRadius: "8px",
                    borderTopLeftRadius: "8px",
                    // Filet solide #EAEAEA, pas un pointillé (Figma 6848:10223).
                    borderBottom: `1px solid ${TOOLBAR_BORDER}`,
                    minHeight: 40,
                }}
            >

                <Stack direction="row" alignItems="center" spacing="8px">
                    <IconButton
                        onClick={() => apiRef.current?.showPreferences("columns")}
                        sx={{
                            width: 24,
                            height: 24,
                            padding: '4px',
                            borderRadius: '8px',
                            color: FOOTER_TEXT,
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            }
                        }}
                    >
                        <Iconify icon="mingcute:calendar-week-line" width={16} height={16} />
                    </IconButton>
                    <IconButton
                        onClick={() => apiRef.current?.showFilterPanel()}
                        sx={{
                            width: 24,
                            height: 24,
                            padding: '4px',
                            borderRadius: '8px',
                            color: FOOTER_TEXT,
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            }
                        }}
                    >
                        <Iconify icon="ic:round-filter-list" width={16} height={16} />
                    </IconButton>
                    <IconButton
                        onClick={() => apiRef.current?.exportDataAsCsv({ fileName: 'local_visibility', utf8WithBom: true })}
                        sx={{
                            width: 24,
                            height: 24,
                            padding: '4px',
                            borderRadius: '8px',
                            color: FOOTER_TEXT,
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            }
                        }}
                    >
                        <Iconify icon="lets-icons:export" width={16} height={16} />
                    </IconButton>
                </Stack>

                {/* Période et comparaison vivent ici, pas dans le header : le
                    date picker global est masqué sur cette page. */}
                <Stack direction="row" alignItems="center" spacing="12px" sx={{ flexShrink: 0 }}>
                    <ToolbarSelect
                        value={dateRangeOption}
                        options={DATE_RANGE_OPTIONS}
                        onChange={setDateRangeOption}
                    />
                    <ToolbarSelect
                        value={comparisonOption}
                        options={COMPARISON_OPTIONS}
                        onChange={setComparisonOption}
                    />
                </Stack>
            </Stack>
        )
    }

    // Le rechargement de la grid ne concerne pas les cartes, et le refetch de la
    // liste pays non plus : les inclure ici les faisait passer en skeleton à chaque
    // changement de période, alors que leur fenêtre de 12 semaines n'a pas bougé.
    // Reste le premier chargement, où il n'y a pas encore de pays à interroger.
    const cardBarLoading = isFetchingVisibilityCard || !selectedCountry;

    return (
        <Box>
            <CountryFilter
                loading={isLoading}
                countries={countriesData}
                selectedCountry={selectedCountry}
                onCountryChange={setSelectedCountry}
                backgroundColor="transparent"
                indicator="radio"
            />

            <Box px={'24px'} mt={isLoading ? '24px' : (countriesData && countriesData?.length > 1) ? '24px' : '0px'}>
                <CardBar
                    blendedPresenceTitle="Visibility Rate"
                    blendedPresenceTopMetric={blendedPresenceTopMetric}
                    blendedPresenceWeeklySeries={blendedPresenceRateWeeklySeries}
                    soloBidderTitle="Solo bidder rate"
                    soloBidderValue={soloBidderValue}
                    soloBidderWeeklySeries={soloBidderWeeklySeries}
                    topCompetitorsTitle="Top Competitors"
                    topCompetitorsData={topCompetitorsData}
                    dateRange="12 weeks"
                    loading={cardBarLoading}
                    selectedCountry={selectedCountry}
                    useNonBrandLayout
                />
                <Paper sx={{
                    p: 0,
                    overflow: 'hidden',
                    mt: 2,
                    // Enhanced border visibility for light mode - englobes both filter and DataGrid - lighter
                    border: `1px solid ${theme.palette.mode === 'dark' ? '#919eab33' : '#f0f0f0'}`,
                    borderRadius: 1,
                }}>

                    <Box
                        sx={{
                            zIndex: -100,
                            width: '100%',
                            // No border needed here - parent Paper already has the border
                            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                                outline: 'none !important',
                            },
                            '& .MuiDataGrid-cell': {
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                // Override des bordures dashed par des bordures solides - lighter
                                borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#919eab33' : '#f0f0f0'} !important`,
                            },
                            '& .leftAlignCell': {
                                justifyContent: 'flex-start !important',
                            },
                            '& .leftAlignCell .MuiDataGrid-cellContent': {
                                justifyContent: 'flex-start !important',
                                textAlign: 'left',
                                width: 'auto',
                                marginRight: 'auto',
                            },
                            '& .MuiDataGrid-row': {
                                alignItems: 'center',
                            },
                            '& .MuiDataGrid-columnHeader': {
                                display: 'flex',
                                alignItems: 'center',
                                // Enhanced header border visibility - lighter
                                borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#919eab33' : '#f0f0f0'} !important`,
                            },
                            '& .MuiDataGrid-columnHeader--alignLeft .MuiDataGrid-columnHeaderTitleContainer': {
                                justifyContent: 'flex-start',
                            },
                            '& .MuiDataGrid-cellContent': {
                                display: 'flex',
                                alignItems: 'center',
                                height: '100%',
                                width: '100%',
                            },
                            // Suppression de la bordure du toolbar container
                            '& .MuiDataGrid-toolbarContainer': {
                                borderBottom: 'none !important',
                            },
                        }}
                    >
                        {/* Hors du slot `toolbar` de la grid : les deux dropdowns sont le
                            seul moyen de changer de fenêtre sur cette page, et la grid
                            n'est pas rendue pendant le chargement, en erreur, ni quand
                            la fenêtre courante ne ramène rien. Les rendre depuis le slot
                            les faisait disparaître exactement quand on en a besoin. */}
                        <CustomToolbar />
                        {isFetchingLocalKpis || isLoading ? <Skeleton variant="rectangular" height={500} /> : hasError ? (
                            // Error fallback UI for DataGrid rendering issues during rapid switching
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="h6" color="error" gutterBottom>
                                    {t('dashboard.visibility.dataGridError.title')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {t('dashboard.visibility.dataGridError.message')}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    onClick={() => setHasError(false)}
                                >
                                    {t('dashboard.visibility.dataGridError.retry')}
                                </Button>
                            </Box>
                        ) : hasNoData ? (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('no_data_available')}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{
                                "& .MuiDataGrid-columnHeader": {
                                    backgroundColor: 'rgba(226, 229, 233, 0.16)!important'
                                },
                                "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                                    color: 'rgba(99, 115, 129, 1)!important'
                                },
                                "& .MuiDataGrid-cell": {
                                    border: "1px solid rgba(145, 158, 171, 0.20)"
                                },
                                // Footer de pagination — Figma 6848:11211. Les défauts MUI
                                // (52px, 13px, texte text.secondary) ne correspondent ni aux
                                // proportions ni aux couleurs. Mêmes valeurs que la grid
                                // Explore, qui suit la même maquette.
                                "& .MuiDataGrid-footerContainer": {
                                    backgroundColor: FOOTER_BG,
                                    minHeight: 40,
                                    height: 40,
                                    "& .MuiToolbar-root": {
                                        minHeight: 40,
                                        height: 40,
                                        paddingRight: "24px",
                                    },
                                    // 14px / lh 22 / #919EAB sur tous les libellés du footer.
                                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiInputBase-root": {
                                        color: FOOTER_TEXT,
                                        fontSize: 14,
                                        fontWeight: 400,
                                        lineHeight: "22px",
                                    },
                                    // « 10 » est une pill bordée dans la maquette, pas un select nu.
                                    "& .MuiTablePagination-input": {
                                        border: `1px solid ${GRID_BORDER}`,
                                        borderRadius: "6px",
                                        height: 30,
                                        marginLeft: "12px",
                                        // 32px entre le groupe « Rows per page » et « 1–10 of 21 ».
                                        marginRight: "32px",
                                        paddingLeft: "8px",
                                        // Chevron 16px comme la maquette (défaut MUI : 24px).
                                        "& .MuiSelect-icon": {
                                            width: 16,
                                            height: 16,
                                            top: "calc(50% - 8px)",
                                            color: FOOTER_TEXT,
                                        },
                                    },
                                    "& .MuiTablePagination-actions": {
                                        marginLeft: "8px",
                                        "& .MuiIconButton-root": {
                                            width: 34,
                                            height: 34,
                                            color: FOOTER_TEXT,
                                        },
                                    },
                                },
                            }}>
                                <DataGridPro
                                    // Unique key forces complete remount on workspace/country change, preventing ARIA conflicts
                                    key={`local-datagrid-${workspace?.id || 'default'}-${selectedCountry || 'all'}`}
                                    apiRef={apiRef}
                                    autoHeight
                                    disableDensitySelector
                                    checkboxSelection={false}
                                    disableRowSelectionOnClick
                                    showCellVerticalBorder
                                    rows={rows}
                                    columns={columns}
                                    // 36px header row (default is 56).
                                    columnHeaderHeight={36}
                                    sortingMode="client"
                                    disableMultipleColumnsSorting
                                    sortingOrder={['desc', 'asc']}
                                    sortModel={sortModel}
                                    onSortModelChange={(newModel) => {
                                        try {
                                            setSortModel(newModel);
                                        } catch (error) {
                                            console.warn('Error in onSortModelChange:', error);
                                        }
                                    }}
                                    pagination
                                    pageSizeOptions={[10, 20, 50, 100]}
                                    initialState={{
                                        pagination: {
                                            paginationModel: { pageSize: 10, page: 0 },
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
}
